import React, { useState, useEffect } from "react";
import LoadingSpinner from "@vfuk/core-loading-spinner";
import AlertNotification from "@vfuk/core-alert-notification"
import SelectInputWithLabel from '@vfuk/core-select-input-with-label';
import SearchInput from '@vfuk/core-search-input';
import SimpleGrid from "@vfuk/core-simple-grid/dist/SimpleGrid";
import { KOA_API_ENDPOINT, OPCO_CODE, LANG_CODE } from "../../SectionAssessment/SectionAssessmentSurvey/SectionAssessmentBusinessGoals/SectionAssessmentBusinessGoals";
import { navigate } from 'gatsby';
import { SearchInputSectionProps, CategoryObject } from "../Types/SearchInputSectionTypes";
import twinxClient from '../ProtectedApiClient';
import { SearchHeaderText, LoadingSpinnerWrapper, SimpleGridWrapper } from "./styles/SearchInputSection.styles";

const SearchInputSection = ({ content, retry, propSearchText, getDocumentListAPI = "", navigateSlug = "", cancel, category, setCategory }: SearchInputSectionProps) => {
  const [searchText, setSearchText] = useState<string | undefined>("");
  const [categoryList, setCategoryList] = useState<CategoryObject[]>([]);
  const [defaultSelectedCategoryIndex, setDefaultSelectedCategoryIndex] = useState<number>(3);
  const [emptySearchErrorAlert, setEmptySearchErrorAlert] = useState<boolean>(false);
  const [selectValue, setSelectValue] = useState<string>("No Data");

  const getListOfCategorieskoa = async () => {
    setCategoryList([]);
    let categoryListObj: any;
    const query: any = {
      Opco: OPCO_CODE,
      Language: LANG_CODE
    };
    const params = new URLSearchParams(query).toString();

    try {
      const { data } = await twinxClient.get(`${KOA_API_ENDPOINT}/categorylist?${params}`);
      if (data.result.length > 0 && !data.result.fault) {
        categoryListObj = data.result.map((item: any) => {
          return { value: item.name, text: item.name };
        });
        if (window.history.state !== null) {
          const selectedCategory = window.history.state.category;
          const defaultCategory = categoryListObj.length >= 3 ? categoryListObj[3].value : categoryListObj[0].value;
          const data = selectedCategory !== undefined ? selectedCategory : defaultCategory;
          const indexOfSelectedCategory = categoryListObj.findIndex((i: any) => i.value === data);
          setDefaultSelectedCategoryIndex(indexOfSelectedCategory);
        }
        setCategoryList(categoryListObj);
        if (categoryListObj[3]) {
          setSelectValue(categoryListObj[defaultSelectedCategoryIndex].value);
        }
      } else {
        setCategoryList([{ value: "No Data", text: "No Data" }]);
      };
    }
    catch (error) {
      console.log(error);
      setCategoryList([{ value: "No Data", text: "No Data" }]);
    };
  };

  const getResultsByClickingEnterKey = async (e: any) => {
    const defaultCategory = categoryList.length >= 3 ? categoryList[3].value : categoryList[0].value;
    const state: any = {
      category: category ? category : defaultCategory,
      text: searchText
    }
    const url: any = navigateSlug
    history.pushState(state, '', url);
    if (e.key === 'Enter') {
      if (!searchText) {
        setEmptySearchErrorAlert(true);
        setTimeout(() => { setEmptySearchErrorAlert(false); }, 1000);
      } else {
        if (getDocumentListAPI !== "") {
          await cancel();
          getDocumentListAPI(searchText, category);
        } else {
          await navigate(navigateSlug, {
            state: {
              category: category === '' ? defaultCategory : category,
              text: searchText
            },
          });
        }
      }
    }
  };

  const searchHandler = async () => {
    if (!searchText || searchText === '') {
      setEmptySearchErrorAlert(true)
      return;
    }
    const defaultCategory = categoryList.length >= 3 ? categoryList[3].value : categoryList[0].value;
    const state: any = {
      category: category === '' ? defaultCategory : category,
      text: searchText
    };
    const url: string = navigateSlug;
    history.pushState(state, '', url);
    if (getDocumentListAPI !== "") {
      await cancel();
      getDocumentListAPI(searchText, category);
    } else {
      await navigate(navigateSlug, {
        state: {
          category: category === '' ? defaultCategory : category,
          text: searchText
        },
      });
    };
  };

  const onClear = () => {
    setSearchText("");
  };

  const inputOnchange = (e: any) => {
    e.preventDefault();
    const text = e.target.value;
    const trimmedText = text.trim().split(/ +/).join(' ');
    setSearchText(trimmedText !== "" ? text : "");
  };

  useEffect(() => {
    getListOfCategorieskoa();
    if (searchText === "") {
      if (window.history.state !== null) {
        setCategory(window.history.state.category);
        setSearchText(window.history.state.text);
      };
    };
  }, []);

  useEffect(() => {
    setSearchText(propSearchText)
  },[propSearchText]);

  useEffect(() => {
    getListOfCategorieskoa();
  },[retry]);

  const fireFilterBySmartSearchCategoryEvent = (selectedCategory: string) => {
    try {
      // fire the utag link event
      if ((window as CustomWindow).onFilterBySmartSearchCategoryEvent) {
        (window as CustomWindow).onFilterBySmartSearchCategoryEvent!(selectedCategory);
      }
    } catch (e) {
      console.log(
        'onFilterBySmartSearchCategoryEvent failed - please check the Filter by Smart Search Category Script'
      );
    }
  };

  const handleChange = (event: any) => {
    setSelectValue(event.target.value);
    setCategory(event.target.value);
    fireFilterBySmartSearchCategoryEvent(event.target.value);
  };

  return (
    <SimpleGridWrapper searchDisabled={!searchText}>
      {
        content.searchHeader !== 'no_content' && <SearchHeaderText>{content.searchHeader}</SearchHeaderText>
      }
      {emptySearchErrorAlert && <div className="search-input-alert-section" >
        <div className="search-input-alert-error">
          <AlertNotification
            button={{
              text: 'Close',
              onClick: () => {
                setEmptySearchErrorAlert(false)
              },
            }}
            state="warn">Please Enter your question</AlertNotification>
        </div>
      </div>}
      <SimpleGrid
        layout={{
          sm: [12],
          md: [4, 8],
          lg: [3, 9],
        }}
      >
        <div>
          {
            categoryList.length === 0 ?
              <LoadingSpinnerWrapper>
                <LoadingSpinner size={5} appearance='secondary' />
              </LoadingSpinnerWrapper>
              :
              <SelectInputWithLabel
                selectInput={{
                  name: 'defaultName',
                  id: 'select',
                  value: selectValue,
                  onChange: (e) => handleChange(e),
                  options:categoryList,
                }}
                fieldWrapper= {{
                  label:''
                }}
              />
          }
        </div>

        <div>

          <div className="search-input" onKeyDown={getResultsByClickingEnterKey} >
            <SearchInput
              textInput={{
                id:'search',
                value: searchText,
                onChange: inputOnchange,
                placeholder: content.searchInputField.placeholder
              }}
              onClear={onClear}
              searchIconButton={{ onClick: searchHandler }}
              fieldWrapper={{
                label: ''
              }}
            />
          </div>
        </div>
      </SimpleGrid>
    </SimpleGridWrapper>
  );
};
export default SearchInputSection;